/**
 * BreakingSecurityAlert.jsx — Bandeau alerte sécurité routière auto-expirant.
 *
 * Extrait de `pages/Home.jsx` (juin 2026) pour réduire la taille du fichier
 * principal. Affiche un bandeau rouge animé pointant vers une analyse
 * éditoriale d'actualité. Auto-expire à la date `expiresAt` et persiste un
 * dismiss manuel 7 jours dans localStorage.
 *
 * Configuration centrale dans `config` ci-dessous — c'est l'unique endroit
 * à modifier pour changer le message, l'URL cible ou la durée.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function BreakingSecurityAlert() {
  const config = {
    id: 'barometre-isere-mai-2026',
    expiresAt: new Date('2026-07-15T23:59:59Z'),
    kicker: 'Sécurité Routière · Isère',
    text: 'Mai 2026 en Isère : 3 morts sur les routes, dont 2 motards (+33 %)',
    cta: "Lire l'analyse locale →",
    href: '/magazine/barometre-securite-routiere-isere-mai-2026',
  };

  const dismissKey = `breaking-alert-dismissed:${config.id}`;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (new Date() > config.expiresAt) return;
    try {
      const dismissedAt = localStorage.getItem(dismissKey);
      if (dismissedAt) {
        const dismissedDate = new Date(dismissedAt);
        const sevenDaysLater = new Date(dismissedDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        if (new Date() < sevenDaysLater) return;
      }
    } catch { /* localStorage indisponible — afficher quand même */ }
    setIsVisible(true);
  }, [dismissKey, config.expiresAt]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.setItem(dismissKey, new Date().toISOString()); } catch { /* ignore */ }
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <Link
      to={config.href}
      className="block group"
      data-testid="breaking-security-alert"
      aria-label={`${config.kicker} : ${config.text}. Cliquer pour lire l'analyse complète.`}
    >
      <div
        className="relative overflow-hidden rounded-lg border border-red-600/40 bg-gradient-to-r from-red-950/95 via-red-900/90 to-red-950/95 px-4 py-3 sm:px-5 sm:py-3.5 shadow-lg shadow-red-900/30 transition-all hover:shadow-xl hover:shadow-red-900/50 hover:border-red-500/60"
        style={{ animation: 'breakingPulse 3s ease-in-out infinite' }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)' }}
        />

        <div className="relative flex items-center gap-3 sm:gap-4">
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
          </span>

          {/* MOBILE : texte unique compact (kicker fusionné avec le titre) */}
          <span className="sm:hidden flex-1 min-w-0 text-xs font-semibold text-white leading-snug">
            <span className="font-black text-red-200">🚨 {config.kicker} —</span>{' '}
            {config.text}
          </span>

          {/* DESKTOP : kicker + texte séparés sur la même ligne */}
          <div className="hidden sm:flex flex-1 min-w-0 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-[11px] font-black uppercase tracking-[0.15em] text-red-200 whitespace-nowrap">
              🚨 {config.kicker}
            </span>
            <span className="text-red-400/60">·</span>
            <span className="text-sm font-semibold text-white leading-snug">
              {config.text}
            </span>
          </div>

          <span className="hidden md:inline-flex items-center gap-1 text-xs font-bold text-red-100 group-hover:text-white whitespace-nowrap transition-colors">
            {config.cta}
          </span>

          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 -mr-1 rounded-full text-red-200/70 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
            aria-label="Fermer cette alerte"
            data-testid="breaking-security-alert-dismiss"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes breakingPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.35), 0 10px 25px -10px rgba(127, 29, 29, 0.5); }
          50% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0), 0 10px 25px -10px rgba(127, 29, 29, 0.7); }
        }
      `}</style>
    </Link>
  );
}
